import { deelVan, maakProfiel } from './beweging'
import type { Profiel } from './beweging'
import { C_TEMPO } from './tempo'
import { wachtenOpOmloop } from '../omloop'

/**
 * De tijdlijn van de C-MATIC-pendel (`Palletpendel.tsx`), los van three.js
 * zodat `scripts/controleer-bewegingen.mjs` hem kan toetsen. Eén rondgang:
 *
 *   A  optillen → naar B → afzetten → doorrijden, keren →
 *      leeg terug, onder beide stations door → voorbij A keren → onder A.
 *
 * Sinds 21 oktober op de snelheden van het typeblad (4,3 km/h beladen, 5,4
 * leeg) en met het gedeelde rijprofiel (`beweging.ts`); de rondgang duurt
 * daarmee ±82 s in plaats van 57: drie keer in de omloop van de hal.
 */

/** De twee stations, net buiten de stellingblokken (die lopen tot ±14,55). */
export const A = -16.5
export const B = 16.5
/** Keerpunten: net voorbij de stations, zodat hij niet onder de lading keert. */
export const UIT = 1.3

const HEEN: Profiel = maakProfiel(B - A, C_TEMPO.beladen, C_TEMPO.a)
const TERUG: Profiel = maakProfiel(B + UIT - (A - UIT), C_TEMPO.leeg, C_TEMPO.a)
const DOOR: Profiel = maakProfiel(UIT, C_TEMPO.leeg, C_TEMPO.a)
const KEER: Profiel = maakProfiel(Math.PI, C_TEMPO.draai, C_TEMPO.draaiA)

/** Duur van elke fase behalve de laatste, in de volgorde van de kop. */
const RIJDEN = [C_TEMPO.hef, HEEN.duur, C_TEMPO.hef, DOOR.duur, KEER.duur, TERUG.duur, KEER.duur, DOOR.duur]
/**
 * De laatste fase is een korte rust onder A: een seconde, gerekt tot de
 * rondgang op de omloop van de hal past (`omloop.ts`) — nu ±1,8 s.
 */
const RUST = wachtenOpOmloop(RIJDEN.reduce((t, d) => t + d, 0) + 1, [1])[0]
const DUUR = [...RIJDEN, RUST]

export const RONDGANG = DUUR.reduce((t, d) => t + d, 0)

/** Fasegrenzen als fractie van de rondgang. */
export const F = (() => {
  let t = 0
  return DUUR.map((d) => (t += d) / RONDGANG)
})()

export interface PendelStand {
  z: number
  yaw: number
  /** Stand van de adapterplaat, 0 (omlaag) tot 1 (60 mm omhoog). */
  hef: number
  geladen: boolean
}

const klem = (x: number) => Math.min(1, Math.max(0, x))
const glad = (x: number) => x * x * (3 - 2 * x)

/** Waar de C-MATIC is op fase t (0..1 van een rondgang). */
export function pendelStand(t: number): PendelStand {
  const deel = (i: number) => klem((t - (i ? F[i - 1] : 0)) / (F[i] - (i ? F[i - 1] : 0)))
  const langs = (p: Profiel, i: number) => deelVan(p, deel(i) * p.duur)
  if (t < F[0]) return { z: A, yaw: 0, hef: glad(deel(0)), geladen: true }
  if (t < F[1]) return { z: A + (B - A) * langs(HEEN, 1), yaw: 0, hef: 1, geladen: true }
  if (t < F[2]) return { z: B, yaw: 0, hef: 1 - glad(deel(2)), geladen: true }
  if (t < F[3]) return { z: B + UIT * langs(DOOR, 3), yaw: 0, hef: 0, geladen: false }
  if (t < F[4]) return { z: B + UIT, yaw: Math.PI * langs(KEER, 4), hef: 0, geladen: false }
  if (t < F[5]) return { z: B + UIT + (A - UIT - B - UIT) * langs(TERUG, 5), yaw: Math.PI, hef: 0, geladen: false }
  if (t < F[6]) return { z: A - UIT, yaw: Math.PI * (1 + langs(KEER, 6)), hef: 0, geladen: false }
  if (t < F[7]) return { z: A - UIT + UIT * langs(DOOR, 7), yaw: 0, hef: 0, geladen: false }
  return { z: A, yaw: 0, hef: 0, geladen: false }
}
